import { useEffect, useMemo, useState } from "react";
import { useTheme } from "../contexts/ThemeContext";
import { listClientes } from "../services/clientesService";
import Loading, { ErrorBox } from "../components/ui/Loading";
import ClienteFormModal from "../components/forms/ClienteFormModal";

function initials(nome) {
  if (!nome) return "?";
  return nome
    .split(" ")
    .filter(Boolean)
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export default function ClientesPage() {
  const { C } = useTheme();
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // cliente em edição ou null (modo criação)

  async function reload() {
    setLoading(true);
    try {
      const res = await listClientes();
      setClientes(res);
      setError(null);
    } catch {
      setError("Não foi possível carregar os clientes.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
  }, []);

  const filtered = useMemo(() => {
    if (!search) return clientes;
    const s = search.toLowerCase();
    return clientes.filter(
      (c) =>
        c.nome?.toLowerCase().includes(s) ||
        c.email?.toLowerCase().includes(s) ||
        c.pais?.toLowerCase().includes(s)
    );
  }, [clientes, search]);

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: C.text, margin: 0 }}>
            Clientes
          </h1>
          <p style={{ color: C.textMuted, fontSize: 13, margin: "4px 0 0" }}>
            {clientes.length} turista{clientes.length === 1 ? "" : "s"} cadastrado
            {clientes.length === 1 ? "" : "s"}
          </p>
        </div>
        <button
          onClick={() => {
            setEditing(null);
            setModalOpen(true);
          }}
          style={{
            padding: "10px 20px",
            borderRadius: 10,
            border: "none",
            background: C.accent,
            color: "#fff",
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer",
            fontFamily: "'DM Sans', sans-serif",
          }}
        >
          + Novo Cliente
        </button>
      </div>

      <ClienteFormModal
        open={modalOpen}
        cliente={editing}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        onSaved={reload}
        onDeleted={reload}
      />

      <div style={{ marginBottom: 18 }}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar cliente..."
          style={{
            width: "100%",
            maxWidth: 360,
            padding: "10px 16px",
            borderRadius: 10,
            border: `1px solid ${C.border}`,
            background: C.inputBg,
            color: C.text,
            fontSize: 13,
            outline: "none",
            fontFamily: "'DM Sans', sans-serif",
            boxSizing: "border-box",
          }}
        />
      </div>

      {loading && <Loading />}
      {error && <ErrorBox message={error} />}

      {!loading && !error && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {filtered.map((c) => (
            <div
              key={c.id}
              onClick={() => {
                setEditing(c);
                setModalOpen(true);
              }}
              style={{
                background: C.bgCard,
                borderRadius: 12,
                padding: "16px 20px",
                border: `1px solid ${C.border}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 50,
                    background: `linear-gradient(135deg, ${C.accent}, ${C.accentLight})`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 16,
                    fontWeight: 700,
                    color: C.avatarText,
                  }}
                >
                  {initials(c.nome)}
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>
                    {c.nome}
                  </div>
                  <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>
                    {c.email ?? "—"}
                    {c.pais ? ` · ${c.pais}` : ""}
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <div style={{ textAlign: "right" }}>
                  {c.passaporte && (
                    <div style={{ fontSize: 12, color: C.textMuted }}>
                      Passaporte: {c.passaporte}
                    </div>
                  )}
                  {c.origem && (
                    <div style={{ fontSize: 11, color: C.textDim, marginTop: 2 }}>
                      Origem: {c.origem}
                    </div>
                  )}
                </div>
                <span style={{ color: C.textDim, fontSize: 16 }}>›</span>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div style={{ fontSize: 13, color: C.textMuted, padding: 20, textAlign: "center" }}>
              Nenhum cliente encontrado.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
